import { type ChangeEvent, useCallback } from 'react';
import { type FieldValues, useController } from 'react-hook-form';

import type { ComponentProps } from '~/chords/ComponentProps';
import { FormField, type FormFieldProps } from '~/chords/FormField';
import { type BaseInputProps, Input } from '~/chords/Input';

export type FormFieldInputProps<TFormFields extends FieldValues> =
  ComponentProps<
    Omit<FormFieldProps<TFormFields>, 'children'> &
      Omit<BaseInputProps, 'name' | 'className'> & {
        /**
         * When true, converts the input value to a number before setting it on the form.
         * Useful for number inputs where you want the form value to be a number, not a string.
         */
        valueAsNumber?: boolean;
      }
  >;

export function FormFieldInput<TFormFields extends FieldValues>({
  name,
  id,
  label,
  labelAddon,
  labelAsSpan,
  dangerouslyOverrideClassName,
  disabled = false,
  readOnly = false,
  fieldMessageClassName,
  triggerClassName,
  showErrorMessage = true,
  valueAsNumber = false,
  ...inputProps
}: FormFieldInputProps<TFormFields>) {
  const {
    field,
    fieldState: { invalid },
  } = useController<TFormFields>({ name, disabled });

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (valueAsNumber) {
        const numValue = e.target.valueAsNumber;

        field.onChange(Number.isNaN(numValue) ? '' : numValue);
      } else {
        field.onChange(e);
      }
    },
    [field, valueAsNumber],
  );

  return (
    <FormField
      name={name}
      id={id}
      fieldMessageClassName={fieldMessageClassName}
      label={label}
      labelAddon={labelAddon}
      labelAsSpan={labelAsSpan}
      dangerouslyOverrideClassName={dangerouslyOverrideClassName}
      disabled={disabled}
      readOnly={readOnly}
      showErrorMessage={showErrorMessage}
    >
      <Input.Text
        {...field}
        onChange={handleChange}
        state={invalid ? 'error' : undefined}
        id={id || name}
        {...inputProps}
        disabled={disabled}
        readOnly={readOnly}
        dangerouslyOverrideClassName={{ __className: triggerClassName }}
      />
    </FormField>
  );
}

FormFieldInput.displayName = 'FormFieldInput';
